import type { OrganizationNode, OrganizationStatus, OrganizationType } from '@shared/types';
import { AlertCircle, ArrowLeft, Loader2, Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { apiUrl } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';

const STATUS_VALUES: OrganizationStatus[] = ['ACTIVE', 'STOPPED_RECRUITMENT', 'ABOLISHED_SCHEDULED', 'ABOLISHED'];
const API_BASE = apiUrl('/api');

// Helper to flatten tree for dropdown
const flattenTree = (
  nodes: OrganizationNode[],
  depth = 0,
  result: { id: string; name: string; depth: number }[] = [],
) => {
  for (const node of nodes) {
    if (node.currentDefinition) {
      result.push({
        id: node.id,
        name: node.currentDefinition.nameFormal,
        depth,
      });
      if (node.children) {
        flattenTree(node.children, depth + 1, result);
      }
    }
  }
  return result;
};

export default function OrganizationDetail() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedTenant } = useTenant();

  // Determine effective tenant ID
  const effectiveTenantId = useMemo(() => {
    if (!user) return null;
    if (['system_admin', 'system_operator', 'system_support'].includes(user.role)) {
      return selectedTenant?.id || null;
    }
    return user.tenantId;
  }, [user, selectedTenant]);

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data State
  const [node, setNode] = useState<OrganizationNode | null>(null);
  const [parentOptions, setParentOptions] = useState<{ id: string; name: string; depth: number }[]>([]);

  // Form State
  const [formData, setFormData] = useState({
    type: 'DEPARTMENT' as OrganizationType,
    code: '',
    nameFormal: '',
    nameShort: '',
    nameKana: '',
    nameEn: '',
    displayOrder: 0,
    departmentCode: '',
    status: 'ACTIVE' as OrganizationStatus,
    validFrom: new Date().toISOString().split('T')[0], // Today
    validTo: '',
    abolitionDate: '',
    parentStructureId: '',
  });

  const [activeTab, setActiveTab] = useState<'detail' | 'history'>('detail');

  // Fetch Parent Options (Organization Tree)
  useEffect(() => {
    if (!effectiveTenantId) return;

    const fetchTree = async () => {
      try {
        const res = await fetch(`${API_BASE}/organizations?tenantId=${effectiveTenantId}&academicYear=2025`); // Default year
        if (res.ok) {
          const tree: OrganizationNode[] = await res.json();
          setParentOptions(flattenTree(tree));
        }
      } catch (e) {
        console.error('Failed to fetch org tree for parents', e);
      }
    };
    fetchTree();
  }, [effectiveTenantId]);

  // Fetch Detail if Edit Mode
  useEffect(() => {
    if (isNew || !id) return;

    const fetchDetail = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/organizations/${id}`);
        if (!res.ok) throw new Error('Failed to fetch organization');

        const data: OrganizationNode = await res.json();
        setNode(data);

        if (data.currentDefinition) {
          const def = data.currentDefinition;
          setFormData({
            type: data.type,
            code: def.code,
            nameFormal: def.nameFormal,
            nameShort: def.nameShort,
            nameKana: def.nameKana || '',
            nameEn: def.nameEn || '',
            displayOrder: def.displayOrder,
            departmentCode: def.departmentCode || '',
            status: def.status,
            validFrom: def.validFrom,
            validTo: def.validTo || '',
            abolitionDate: def.abolitionDate || '',
            parentStructureId: def.parentStructureId || '',
          });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetail();
  }, [id, isNew]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'displayOrder' ? Number(value) : value,
    }));
  };

  const handleSave = async () => {
    if (!effectiveTenantId) {
      setError('Tenant ID missing');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const url = isNew ? `${API_BASE}/organizations` : `${API_BASE}/organizations/${id}`;
      const method = isNew ? 'POST' : 'PUT';

      const payload = {
        ...formData,
        tenantId: effectiveTenantId, // Required for creation
        // Clean up empty strings to null/undefined where appropriate if needed, or backend handles it
        // Ensure parentStructureId is null if empty
        parentStructureId: formData.parentStructureId || null,
        nameEn: formData.nameEn || null,
        departmentCode: formData.departmentCode || null,
        validTo: formData.validTo || null,
        abolitionDate: formData.abolitionDate || null,
      };

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = (await res.json()) as { message?: string };
        throw new Error(errData.message || 'Failed to save');
      }

      // Success
      const saved: OrganizationNode = await res.json();
      alert('Success!');
      if (isNew) {
        navigate(`/site/organizations/${saved.id}`);
      } else {
        // Refresh?
        setNode(saved); // Update local state
      }
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Back Link */}
      <div className="mb-4">
        <Link to="/site/organizations" className="flex items-center text-sm text-gray-500 hover:text-blue-600">
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('organizations.title')}
        </Link>
      </div>

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">{isNew ? 'New Organization' : t('common.edit')}</h1>
          {!isNew && node && <p className="text-gray-500 text-sm mt-1">{node.currentDefinition?.nameFormal}</p>}
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="outline" onClick={() => navigate(`/site/organizations/${id}/change`)}>
              {t('menu.changeRequests')}
            </Button>
          )}
          <Button onClick={handleSave} disabled={isSaving} className="gap-2">
            {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
            <Save className="w-4 h-4" />
            {t('common.save')}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <div className="border-b mb-6">
        <button
          type="button"
          className={`px-4 py-2 font-medium ${
            activeTab === 'detail' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'
          }`}
          onClick={() => setActiveTab('detail')}
        >
          {t('organizations.sections.identification')}
        </button>
        {!isNew && (
          <button
            type="button"
            className={`px-4 py-2 font-medium ${
              activeTab === 'history' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'
            }`}
            onClick={() => setActiveTab('history')}
          >
            {t('organizations.sections.history')}
          </button>
        )}
      </div>

      {activeTab === 'detail' && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
          {/* Structure Info (Immutable usually, but type is selectable on creation) */}
          <section className="bg-white border rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4 text-gray-800">Basic Information</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="org-type" className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Type
                </label>
                <select
                  id="org-type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  disabled={!isNew} // Cannot change type after creation usually
                  className="w-full border rounded-md px-3 py-2 bg-white"
                >
                  <option value="UNIVERSITY">University</option>
                  <option value="CAMPUS">Campus</option>
                  <option value="FACULTY">Faculty</option>
                  <option value="DEPARTMENT">Department</option>
                  <option value="COURSE">Course</option>
                  <option value="MAJOR">Major</option>
                </select>
              </div>
              <div>
                <label htmlFor="parent-org" className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Organization
                </label>
                <select
                  id="parent-org"
                  name="parentStructureId"
                  value={formData.parentStructureId}
                  onChange={handleInputChange}
                  className="w-full border rounded-md px-3 py-2 bg-white"
                >
                  <option value="">(None - Root)</option>
                  {parentOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {'-'.repeat(opt.depth)} {opt.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-4">
              <div>
                <label htmlFor="org-code" className="block text-sm font-medium text-gray-700 mb-1 required">
                  Code
                </label>
                <input
                  id="org-code"
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  className="w-full border rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label htmlFor="dept-code" className="block text-sm font-medium text-gray-700 mb-1">
                  Department Code (Internal)
                </label>
                <input
                  id="dept-code"
                  type="text"
                  name="departmentCode"
                  value={formData.departmentCode}
                  onChange={handleInputChange}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="e.g. MD01"
                />
              </div>
            </div>
          </section>

          {/* Names Section */}
          <section className="bg-white border rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4 text-gray-800">{t('organizations.sections.names')}</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="name-formal" className="block text-sm font-medium text-gray-700 mb-1 required">
                  {t('organizations.fields.formalName')}
                </label>
                <input
                  id="name-formal"
                  type="text"
                  name="nameFormal"
                  value={formData.nameFormal}
                  onChange={handleInputChange}
                  className="w-full border rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label htmlFor="name-en" className="block text-sm font-medium text-gray-700 mb-1">
                  Alertnative/English Name
                </label>
                <input
                  id="name-en"
                  type="text"
                  name="nameEn"
                  value={formData.nameEn}
                  onChange={handleInputChange}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="e.g. Faculty of Medicine"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name-short" className="block text-sm font-medium text-gray-700 mb-1 required">
                    {t('organizations.fields.shortName')}
                  </label>
                  <input
                    id="name-short"
                    type="text"
                    name="nameShort"
                    value={formData.nameShort}
                    onChange={handleInputChange}
                    className="w-full border rounded-md px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="name-kana" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('organizations.fields.kanaName')}
                  </label>
                  <input
                    id="name-kana"
                    type="text"
                    name="nameKana"
                    value={formData.nameKana}
                    onChange={handleInputChange}
                    className="w-full border rounded-md px-3 py-2"
                    placeholder="カナ"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Display & Order Section */}
          <section className="bg-white border rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4 text-gray-800">{t('organizations.sections.display')}</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="display-order" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('organizations.fields.displayOrder')}
                </label>
                <input
                  id="display-order"
                  type="number"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleInputChange}
                  className="w-full border rounded-md px-3 py-2"
                  min={0}
                />
              </div>
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('organizations.status')}
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full border rounded-md px-3 py-2 bg-white"
                >
                  {STATUS_VALUES.map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* Validity Section */}
          <section className="bg-white border rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4 text-gray-800">{t('organizations.sections.validity')}</h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="valid-from" className="block text-sm font-medium text-gray-700 mb-1 required">
                  {t('organizations.validFrom')}
                </label>
                <input
                  id="valid-from"
                  type="date"
                  name="validFrom"
                  value={formData.validFrom}
                  onChange={handleInputChange}
                  className="w-full border rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label htmlFor="valid-to" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('organizations.validTo')}
                </label>
                <input
                  id="valid-to"
                  type="date"
                  name="validTo"
                  value={formData.validTo}
                  onChange={handleInputChange}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label htmlFor="abolition-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Abolition Date
                </label>
                <input
                  id="abolition-date"
                  type="date"
                  name="abolitionDate"
                  value={formData.abolitionDate}
                  onChange={handleInputChange}
                  className="w-full border rounded-md px-3 py-2"
                  disabled={formData.status !== 'STOPPED_RECRUITMENT' && formData.status !== 'ABOLISHED_SCHEDULED'}
                />
              </div>
            </div>
          </section>
        </div>
      )}

      {activeTab === 'history' && !isNew && (
        <div className="p-12 text-center text-gray-500 bg-gray-50 rounded-lg border">
          <p>History view not yet implemented.</p>
        </div>
      )}
    </div>
  );
}
